import { useQueryClient } from "@tanstack/react-query"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { ButtonWarning } from "components/ui/buttons/ButtonWarning/ButtonWarning"
import { useUpdateModelNumberCategories } from "hooks/api/modelNumbers/modelNumbers.hook"
import { useObjectCategoryDrawerStore } from "store/configuration.store"
import { useMessage } from "hooks/message.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import style from "./DetachModelNumberModal.module.scss"

const { text, buttons } = style

type Props = {
	isModalOpen: boolean
	modelNumberId?: number
	modelNumberName?: string
	onCancel: () => void
}

export const DetachModelNumberModal = ({ isModalOpen, modelNumberId, modelNumberName, onCancel }: Props) => {
	const queryClient = useQueryClient()
	const { objectCategoryStore, setObjectCategory, modelNumberToId, setModelNumberToId } = useObjectCategoryDrawerStore()
	const { success } = useMessage()
	const { formatError } = useApiErrorFormatter()
	const { mutate: updateModelNumberCategories, isPending } = useUpdateModelNumberCategories()

	const onDetachHandle = () => {
		if (!modelNumberId || !modelNumberName) return

		updateModelNumberCategories(
			{ id: modelNumberId, relatedCategoryId: null },
			{
				onSuccess: () => {
					const updatedDimensions = { ...objectCategoryStore.dimensions }
					delete updatedDimensions[modelNumberName]
					setObjectCategory({
						...objectCategoryStore,
						dimensions: updatedDimensions,
						modelNumbers: objectCategoryStore.modelNumbers.filter((name) => name !== modelNumberName),
					})

					const updatedModelNumberToId = { ...modelNumberToId }
					delete updatedModelNumberToId[modelNumberName]
					setModelNumberToId(updatedModelNumberToId)

					void Promise.all([
						queryClient.invalidateQueries({ queryKey: ["objectsCategories"] }),
						// The detached model is linkable again, so the Link modal's list must refetch.
						queryClient.invalidateQueries({ queryKey: ["getModelNumbers"] }),
					])
					success("Model number detached from object")
					onCancel()
				},
				onError: (e) => {
					formatError(e)
				},
			},
		)
	}

	return (
		<Modal title="Detach model from object" isModalOpen={isModalOpen} setModalClose={onCancel} width={456}>
			<p className={text}>
				Are you sure you want to detach model number <span>{modelNumberName ?? ""}</span> from object?
			</p>
			<div className={buttons}>
				<ButtonSecondary onClick={onCancel}>Cancel</ButtonSecondary>
				<ButtonWarning onClick={onDetachHandle} submittable={!isPending}>
					Yes, detach
				</ButtonWarning>
			</div>
		</Modal>
	)
}
